/**
 * Account bottom-sheet for the Saved page: create an account, sign in, or
 * change the PIN. Username + 6-digit PIN only — see
 * docs/spikes/optional-accounts-username-pin.md.
 */

import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { checkUsername } from '@/api/account';
import { RateLimitError, UnauthorizedError } from '@/api/errors';
import { useDebouncedValue } from '@/lib/useDebouncedValue';

type View = 'create' | 'signin' | 'changePin';
type Availability = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

interface AccountSheetProps {
  /** 'create' opens the create/sign-in pair; 'changePin' opens PIN rotation. */
  mode: 'create' | 'changePin';
  onClose: () => void;
  onClaim: (username: string, pin: string) => Promise<void>;
  onSignIn: (username: string, pin: string) => Promise<void>;
  onChangePin: (currentPin: string, newPin: string) => Promise<void>;
  t: TFunction;
}

const sheetStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: '#fbf8f3',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  padding: '20px 20px max(24px, env(safe-area-inset-bottom))',
  fontFamily: '-apple-system, system-ui',
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  fontSize: 16, // ≥16 avoids iOS zoom-on-focus
  border: '1px solid #e2ddd4',
  borderRadius: 12,
  background: '#fff',
  fontFamily: '-apple-system, system-ui',
};

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '13px 16px',
  borderRadius: 999,
  border: 'none',
  background: disabled ? '#caa' : '#d23',
  color: '#fff',
  fontSize: 15,
  fontWeight: 800,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#5a544c',
  margin: '14px 4px 6px',
};

// Masks digits while keeping the numeric keypad (type stays "text", so
// inputMode="numeric" still triggers the number pad — unlike type="password").
const PIN_MASK = { WebkitTextSecurity: 'disc' } as unknown as React.CSSProperties;

function onlyDigits(v: string): string {
  return v.replace(/\D/g, '').slice(0, 6);
}

/** Label row for a PIN field with an inline show/hide toggle. */
function PinLabel({
  text,
  show,
  onToggle,
  showText,
  hideText,
}: {
  text: string;
  show: boolean;
  onToggle: () => void;
  showText: string;
  hideText: string;
}) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '14px 4px 6px' }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: '#5a544c' }}>{text}</span>
      <button
        type="button"
        onClick={onToggle}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#d23',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {show ? hideText : showText}
      </button>
    </div>
  );
}

export function AccountSheet({ mode, onClose, onClaim, onSignIn, onChangePin, t }: AccountSheetProps) {
  const [view, setView] = useState<View>(mode === 'changePin' ? 'changePin' : 'create');
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability>('idle');
  const [showPin, setShowPin] = useState(false);

  const debouncedUsername = useDebouncedValue(username, 400);

  // Advisory availability check (create view only). The server's unique index
  // is the real guard — claim handles a lost race.
  useEffect(() => {
    if (view !== 'create') return;
    if (!debouncedUsername) {
      setAvailability('idle');
      return;
    }
    if (!USERNAME_RE.test(debouncedUsername)) {
      setAvailability('invalid');
      return;
    }
    let cancelled = false;
    setAvailability('checking');
    void checkUsername(debouncedUsername)
      .then((ok) => {
        if (!cancelled) setAvailability(ok ? 'available' : 'taken');
      })
      .catch(() => {
        if (!cancelled) setAvailability('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedUsername, view]);

  const title =
    view === 'changePin'
      ? t('saved:account.changePinTitle')
      : view === 'signin'
        ? t('saved:account.signInTitle')
        : t('saved:account.createTitle');

  const sub =
    view === 'signin' ? t('saved:account.signInSub') : view === 'create' ? t('saved:account.createSub') : null;

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      if (view === 'create') {
        if (pin !== pinConfirm) {
          setError(t('saved:account.pinMismatch'));
          return;
        }
        await onClaim(username, pin);
        onClose();
      } else if (view === 'signin') {
        await onSignIn(username, pin); // reloads on success
      } else {
        await onChangePin(currentPin, newPin);
        onClose();
      }
    } catch (err) {
      if (err instanceof RateLimitError) {
        setError(t('saved:account.tooManyTries'));
      } else if (view === 'changePin') {
        setError(err instanceof UnauthorizedError ? t('saved:account.wrongPin') : t('saved:account.error'));
      } else if (view === 'signin' && err instanceof UnauthorizedError) {
        setError(t('saved:account.invalidCredentials'));
      } else {
        setError(t('saved:account.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    !busy &&
    (view === 'create'
      ? availability === 'available' && pin.length === 6 && pinConfirm.length === 6
      : view === 'signin'
        ? USERNAME_RE.test(username) && pin.length === 6
        : currentPin.length === 6 && newPin.length === 6);

  return (
    <div
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(20, 16, 12, 0.55)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} style={sheetStyle}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1612' }}>{title}</div>
        {sub && <div style={{ fontSize: 13, color: '#5a544c', marginTop: 4 }}>{sub}</div>}

        {/* Username (create + sign in) */}
        {view !== 'changePin' && (
          <>
            <label style={labelStyle}>{t('saved:account.username')}</label>
            <input
              style={fieldStyle}
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              placeholder={t('saved:account.usernamePlaceholder')}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={20}
            />
            {view === 'create' && availability !== 'idle' && (
              <div
                style={{
                  fontSize: 12,
                  marginTop: 6,
                  marginLeft: 4,
                  fontWeight: 600,
                  color: availability === 'available' ? '#0a7f0a' : '#b23',
                }}
              >
                {availability === 'checking' && t('saved:account.checking')}
                {availability === 'available' && t('saved:account.available')}
                {availability === 'taken' && t('saved:account.taken')}
                {availability === 'invalid' && t('saved:account.usernameRule')}
              </div>
            )}
          </>
        )}

        {/* PINs */}
        {view === 'changePin' ? (
          <>
            <PinLabel
              text={t('saved:account.currentPin')}
              show={showPin}
              onToggle={() => setShowPin((s) => !s)}
              showText={t('saved:account.showPin')}
              hideText={t('saved:account.hidePin')}
            />
            <input
              style={showPin ? fieldStyle : { ...fieldStyle, ...PIN_MASK }}
              value={currentPin}
              onChange={(e) => setCurrentPin(onlyDigits(e.target.value))}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={6}
            />
            <label style={labelStyle}>{t('saved:account.newPin')}</label>
            <input
              style={showPin ? fieldStyle : { ...fieldStyle, ...PIN_MASK }}
              value={newPin}
              onChange={(e) => setNewPin(onlyDigits(e.target.value))}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={6}
            />
          </>
        ) : (
          <>
            <PinLabel
              text={t('saved:account.pin')}
              show={showPin}
              onToggle={() => setShowPin((s) => !s)}
              showText={t('saved:account.showPin')}
              hideText={t('saved:account.hidePin')}
            />
            <input
              style={showPin ? fieldStyle : { ...fieldStyle, ...PIN_MASK }}
              value={pin}
              onChange={(e) => setPin(onlyDigits(e.target.value))}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={6}
            />
            {view === 'create' && (
              <>
                <label style={labelStyle}>{t('saved:account.pinConfirm')}</label>
                <input
                  style={showPin ? fieldStyle : { ...fieldStyle, ...PIN_MASK }}
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(onlyDigits(e.target.value))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  maxLength={6}
                />
              </>
            )}
          </>
        )}

        {error && (
          <div style={{ fontSize: 13, color: '#b23', fontWeight: 600, margin: '12px 4px 0' }}>{error}</div>
        )}

        {view === 'create' && (
          <div style={{ fontSize: 11, color: '#8a7f72', margin: '12px 4px 0' }}>
            {t('saved:account.switchNote')}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <button type="button" disabled={!canSubmit} onClick={submit} style={primaryBtn(!canSubmit)}>
            {busy
              ? view === 'changePin'
                ? t('saved:account.changing')
                : view === 'signin'
                  ? t('saved:account.signingIn')
                  : t('saved:account.creating')
              : view === 'changePin'
                ? t('saved:account.changePin')
                : view === 'signin'
                  ? t('saved:account.signIn')
                  : t('saved:account.create')}
          </button>
        </div>

        {/* Toggle between create / sign in */}
        {view !== 'changePin' && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setView(view === 'create' ? 'signin' : 'create');
            }}
            style={{
              display: 'block',
              width: '100%',
              marginTop: 12,
              padding: 8,
              background: 'transparent',
              border: 'none',
              color: '#5a544c',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {view === 'create' ? t('saved:account.haveAccount') : t('saved:account.noAccount')}
          </button>
        )}
      </div>
    </div>
  );
}
